// src/services/afipQueueService.js
/**
 * AFIP Queue Service - Handles async invoice queue when AFIP is offline
 * Backend automatically queues invoices if AFIP health check fails
 * Frontend adapts seamlessly to both instant (200) and queued (202) responses
 */

import axios from 'axios';
import { NODE_BACKEND_API_URL } from '../config';

const AFIP_QUEUE_API_URL = `${NODE_BACKEND_API_URL}/afip/queue`;
const AFIP_HEALTH_API_URL = `${NODE_BACKEND_API_URL}/afip/health`;

/**
 * Get authentication headers from localStorage
 * @returns {Object} Headers with Authorization token
 */
const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
    };
};

/**
 * Generate external reference for DUTTO invoices
 * Format: CASADUTTO-DD-MM-YY-HH-MM-SS
 * This matches the existing pattern used for instant invoices
 * @param {string} organizationName - Organization name (default: CASADUTTO)
 * @returns {string} - Formatted external reference
 */
export const generateExternalReference = (organizationName = 'CASADUTTO') => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = String(now.getFullYear()).slice(-2); // Last 2 digits
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    
    return `${organizationName}-${day}-${month}-${year}-${hours}-${minutes}-${seconds}`;
};

/**
 * Check AFIP health status
 * @returns {Promise<Object>} { success: boolean, data: { online: boolean, lastCheck: string } }
 */
export const checkAFIPHealth = async () => {
    try {
        const response = await axios.get(AFIP_HEALTH_API_URL, {
            headers: getAuthHeaders()
        });
        
        return {
            success: true,
            data: response.data
        };
    } catch (error) {
        console.error('Error checking AFIP health:', error);
        return {
            success: false,
            online: false,
            message: error.response?.data?.message || 'Error checking AFIP status',
            error: error.response?.data
        };
    }
};

/**
 * Submit invoice to async queue
 * NOTE: You should use the regular invoice creation endpoint (/apitest/create)
 * Backend will automatically queue if AFIP is down
 * This method is here for direct queue submission if needed
 * @param {Object} invoiceData - Invoice data
 * @returns {Promise<Object>} { success: boolean, data: { queueId, externalReference, invoiceId } }
 */
export const submitToQueue = async (invoiceData) => {
    try {
        const response = await axios.post(
            `${AFIP_QUEUE_API_URL}/submit`,
            invoiceData,
            { headers: getAuthHeaders() }
        );
        
        return {
            success: true,
            data: response.data
        };
    } catch (error) {
        console.error('Error submitting to queue:', error);
        return {
            success: false,
            message: error.response?.data?.message || 'Error submitting to queue',
            error: error.response?.data
        };
    }
};

/**
 * Check queue status by external reference
 * @param {string} externalReference - External reference (e.g., CASADUTTO-17-12-25-18-56-45)
 * @returns {Promise<Object>} { success: boolean, data: { queue: {...} } }
 */
export const checkStatus = async (externalReference) => {
    try {
        const response = await axios.get(
            `${AFIP_QUEUE_API_URL}/${externalReference}`,
            { headers: getAuthHeaders() }
        );
        
        return {
            success: true,
            data: response.data
        };
    } catch (error) {
        console.error('Error checking queue status:', error);
        return {
            success: false,
            message: error.response?.data?.message || 'Error checking status',
            error: error.response?.data
        };
    }
};

/**
 * Get queue statistics
 * @returns {Promise<Object>} { success: boolean, data: { stats: {...} } }
 */
export const getStats = async () => {
    try {
        const response = await axios.get(
            `${AFIP_QUEUE_API_URL}/stats`,
            { headers: getAuthHeaders() }
        );
        
        return {
            success: true,
            data: response.data
        };
    } catch (error) {
        console.error('Error fetching queue stats:', error);
        return {
            success: false,
            message: error.response?.data?.message || 'Error fetching stats',
            error: error.response?.data
        };
    }
};

/**
 * Get my queued invoices
 * @param {Object} filters - Optional filters { limit, offset }
 * @returns {Promise<Object>} { success: boolean, data: { invoices: [...], total, limit, offset } }
 */
export const getMyInvoices = async (filters = {}) => {
    try {
        const params = new URLSearchParams();
        if (filters.limit) params.append('limit', filters.limit);
        if (filters.offset) params.append('offset', filters.offset);

        const response = await axios.get(
            `${AFIP_QUEUE_API_URL}/my-invoices?${params}`,
            { headers: getAuthHeaders() }
        );
        
        return {
            success: true,
            data: response.data
        };
    } catch (error) {
        console.error('Error fetching my invoices:', error);
        return {
            success: false,
            message: error.response?.data?.message || 'Error fetching invoices',
            error: error.response?.data
        };
    }
};

/**
 * Retry failed invoice
 * @param {number} queueId - Queue ID
 * @returns {Promise<Object>} { success: boolean, data: { message, queueId } }
 */
export const retryInvoice = async (queueId) => {
    try {
        const response = await axios.post(
            `${AFIP_QUEUE_API_URL}/${queueId}/retry`,
            {},
            { headers: getAuthHeaders() }
        );
        
        return {
            success: true,
            data: response.data
        };
    } catch (error) {
        console.error('Error retrying invoice:', error);
        return {
            success: false,
            message: error.response?.data?.message || 'Error retrying invoice',
            error: error.response?.data
        };
    }
};

/**
 * Sync all processing invoices with TusFacturas
 * Queries TusFacturas for each processing invoice and updates database with CAE
 * @returns {Promise<Object>} { success: boolean, data: { synced, failed, skipped, message, details } }
 */
export const syncProcessingInvoices = async () => {
    try {
        const response = await axios.post(
            `${AFIP_QUEUE_API_URL}/sync-processing`,
            {},
            { headers: getAuthHeaders() }
        );
        
        return {
            success: true,
            data: response.data
        };
    } catch (error) {
        console.error('Error syncing processing invoices:', error);
        return {
            success: false,
            message: error.response?.data?.message || 'Error syncing invoices',
            error: error.response?.data
        };
    }
};

/**
 * Export all functions as default service object
 */
const afipQueueService = {
    generateExternalReference,
    checkAFIPHealth,
    submitToQueue,
    checkStatus,
    getStats,
    getMyInvoices,
    retryInvoice,
    syncProcessingInvoices
};

export default afipQueueService;
